/**
 * Layer-driven animation per path (pure; the clock is passed in).
 *
 * The choreographer animates head/tail/alpha/pulse itself through setState.
 * Three things the spec ties to those changes are owned by the layer:
 *
 *   trail   1 while the head is drawing forward (colour behind the head mixes toward
 *           `trail`); when the head arrives, eases to 0 over 640 settle (spec §D.8).
 *           A head that jumps straight to "arrived" (preview, reduced motion) never
 *           shows a trail.
 *   comet   fades in over 160 settle when `comet` turns true and sits on the head —
 *           or on `pulse` when pulse ≥ 0 (the drive, spec §E.6: the whole route stays
 *           drawn while the comet travels at s). When it turns false it freezes where it
 *           is and fades over 420 settle while its core scales 1 → 0.6 (spec §E.1 at Tarr).
 *
 * Reduced motion: every transition is immediate.
 */
import type { RouteVisualState } from "@/contracts/map";
import { COMET_FADE_MS, COMET_FADE_SCALE, TRAIL_SETTLE_MS, isArrived, type Internals } from "./state";

export type Ease = (t: number) => number;

/** Comet fade-in when `comet` turns on (a pop at the origin would read as a glitch). */
export const COMET_IN_MS = 160;

export interface Tween {
  from: number;
  to: number;
  t0: number; //  ms (performance.now clock)
  dur: number; // ms
}

export const tweenAt = (tw: Tween, now: number, ease: Ease): number => {
  const t = tw.dur <= 0 ? 1 : Math.min(1, Math.max(0, (now - tw.t0) / tw.dur));
  return tw.from + (tw.to - tw.from) * ease(t);
};
export const tweenDone = (tw: Tween, now: number): boolean => tw.dur <= 0 || now - tw.t0 >= tw.dur;

export interface PathAnim extends Internals {
  drawing: boolean; //         head has advanced (not arrived) since it was last reset
  trailTw: Tween | null;
  cometTw: Tween | null; //    visibility tween (in or out)
  cometOut: boolean; //        the running comet tween is the fade-out (scale follows it)
}

export const restAnim = (): PathAnim => ({ trail: 0, comet: 0, cometScale: 1, cometM: 0, drawing: false, trailTw: null, cometTw: null, cometOut: false });

/**
 * React to a state change. Mutates and returns `a`. `declaredLengthM` is the caller's lengthM.
 */
/** Where a lit comet sits: on the travelling packet when there is one, else on the head. */
export const cometAt = (s: RouteVisualState): number => (s.pulse >= 0 ? s.pulse : s.head);

export function onStateChange(
  a: PathAnim,
  prev: RouteVisualState,
  next: RouteVisualState,
  declaredLengthM: number,
  now: number,
  reduced: boolean,
): PathAnim {
  const arrivedPrev = isArrived(prev, declaredLengthM);
  const arrivedNext = isArrived(next, declaredLengthM);

  if (next.head !== prev.head) {
    if (!arrivedNext && next.head > prev.head && next.head > next.tail) {
      // drawing forward
      a.drawing = true;
      a.trail = 1;
      a.trailTw = null;
    } else if (arrivedNext && !arrivedPrev && a.drawing) {
      // the head has arrived: the whole line eases to core
      a.drawing = false;
      if (reduced || a.trail <= 0) {
        a.trail = 0;
        a.trailTw = null;
      } else {
        a.trailTw = { from: a.trail, to: 0, t0: now, dur: TRAIL_SETTLE_MS };
      }
    } else if (next.head < prev.head || arrivedNext) {
      // reset, retract of the head, or shown whole without drawing
      a.drawing = false;
      a.trail = 0;
      a.trailTw = null;
    }
  }

  if (next.comet && !prev.comet) {
    a.cometOut = false;
    a.cometScale = 1;
    a.cometM = cometAt(next);
    if (reduced) {
      a.comet = 1;
      a.cometTw = null;
    } else {
      a.cometTw = { from: a.comet, to: 1, t0: now, dur: COMET_IN_MS };
    }
  } else if (!next.comet && prev.comet) {
    a.cometM = cometAt(prev); // freeze where it was
    if (reduced || a.comet <= 0) {
      a.comet = 0;
      a.cometScale = 1;
      a.cometTw = null;
      a.cometOut = false;
    } else {
      a.cometOut = true;
      a.cometTw = { from: a.comet, to: 0, t0: now, dur: COMET_FADE_MS };
    }
  }
  if (next.comet) a.cometM = cometAt(next);
  return a;
}

/** Advance the tweens to `now`. Returns true while something is still animating. */
export function stepAnim(a: PathAnim, now: number, settle: Ease): boolean {
  let busy = false;
  if (a.trailTw) {
    a.trail = tweenAt(a.trailTw, now, settle);
    if (tweenDone(a.trailTw, now)) {
      a.trail = a.trailTw.to;
      a.trailTw = null;
    } else busy = true;
  }
  if (a.cometTw) {
    const tw = a.cometTw;
    a.comet = tweenAt(tw, now, settle);
    if (a.cometOut) {
      const t = tw.from > 0 ? 1 - a.comet / tw.from : 1;
      a.cometScale = 1 - (1 - COMET_FADE_SCALE) * t;
    }
    if (tweenDone(tw, now)) {
      a.comet = tw.to;
      if (a.cometOut) a.cometScale = 1;
      a.cometOut = false;
      a.cometTw = null;
    } else busy = true;
  }
  return busy;
}

/** Finish every running tween at once (reduced motion switched on mid-flight). */
export function settleAnim(a: PathAnim): void {
  if (a.trailTw) a.trail = a.trailTw.to;
  if (a.cometTw) a.comet = a.cometTw.to;
  a.trailTw = null;
  a.cometTw = null;
  a.cometOut = false;
  a.cometScale = 1;
}

/* ─── Frame governor ────────────────────────────────────────────────────── */

/** Frames sampled before the governor decides (≈ 1 s of camera motion at 60 Hz). */
export const GOVERNOR_WINDOW = 60;
/** Drop the glow when the MEDIAN frame interval during camera motion exceeds this (the plan's 4K p95 budget). */
export const GOVERNOR_MEDIAN_MS = 28;
/** Longer gaps are not consecutive frames (tab switch, a paused ticker, a one-off hitch). */
export const GOVERNOR_MAX_GAP_MS = 100;

/**
 * Drops the glow pass on a device that cannot hold the frame budget. Pure; the clock is passed in.
 *
 * Only intervals between two frames drawn while the camera is moving count. MapLibre renders every
 * display frame during camera motion, so those intervals measure the device. Every other frame is
 * paced by whoever asked for it. The ticker deliberately runs idle work (the light packet, spec §E.5)
 * at 30 fps, i.e. 33 ms intervals, and counting those would drop the glow on any machine.
 * The median ignores one-off hitches such as a tile upload or a GC pause.
 */
export class FrameGovernor {
  dropped = false;
  private last = 0;
  private lastMoving = false;
  private moving: number[] = [];
  private recent: number[] = [];

  constructor(
    readonly enabled: boolean,
    readonly windowSize = GOVERNOR_WINDOW,
    readonly limitMs = GOVERNOR_MEDIAN_MS,
  ) {}

  /** Record a rendered frame. `cameraMoving` = map.isMoving() at this frame. Returns true once the glow is dropped. */
  sample(now: number, cameraMoving: boolean): boolean {
    const gap = now - this.last;
    const consecutive = this.last > 0 && gap > 0 && gap <= GOVERNOR_MAX_GAP_MS;
    const bothMoving = cameraMoving && this.lastMoving;
    this.last = now;
    this.lastMoving = cameraMoving;
    if (!consecutive) return this.dropped;
    this.recent.push(gap);
    if (this.recent.length > 30) this.recent.shift();
    if (!bothMoving || !this.enabled || this.dropped) return this.dropped;
    this.moving.push(gap);
    if (this.moving.length > this.windowSize) this.moving.shift();
    if (this.moving.length >= this.windowSize && this.median() > this.limitMs) this.dropped = true;
    return this.dropped;
  }

  /** Median interval of the sampled camera-motion frames (0 before any). */
  median(): number {
    if (!this.moving.length) return 0;
    const s = [...this.moving].sort((a, b) => a - b);
    const h = s.length >> 1;
    return s.length % 2 ? s[h] : (s[h - 1] + s[h]) / 2;
  }

  /** Mean interval of the last ≤ 30 consecutive frames of any kind (diagnostics). */
  frameMsAvg(): number {
    return this.recent.length ? this.recent.reduce((s, v) => s + v, 0) / this.recent.length : 0;
  }
}
